// Eszköz-IGÉNY szerkesztése — a jogosultság-tükör és az előtöltés tiszta segédei (Ilcsa #116, #119).
// A SZERVER a fal (`AssetWorkflowTx.requestUpdate`: 403 / 409); ez csak a felület őszinte tükre, hogy a
// „Szerkesztés" gomb ott legyen, ahol a művelet el is fogadódik, és ne kínáljon olyat, amit a szerver elutasít.
import { type AssetRowSchema } from '@atlas/contracts';
import { type z } from 'zod';

import { type AssetCatalogLookup } from '@/features/assets/catalog';
import { NONE, OFFICE_USAGE, type AssetRequestFormValues } from '@/features/assets/form';

type AssetRow = z.infer<typeof AssetRowSchema>;

/** A bíráló (asset_requests.decide) által a beérkezésig szerkeszthető státuszok (a szerver `EDITABLE_BY_DECIDER`-je). */
const EDITABLE_BY_DECIDER: ReadonlySet<string> = new Set([
  'requested',
  'approved',
  'being_procured',
  'purchased',
]);

/**
 * Szerkesztheti-e a felhasználó az igényt:
 *   • az IGÉNYLŐ a SAJÁT igényét, amíg nincs döntés (`requested`);
 *   • a BÍRÁLÓ (`asset_requests.decide`) a beérkezésig.
 * Elutasított / beérkezett / leltári sor → senki.
 */
export function canEditRequest(
  row: Pick<AssetRow, 'status' | 'requesterId'>,
  sessionIdentityId: string | undefined,
  canDecide: boolean,
): boolean {
  if (!EDITABLE_BY_DECIDER.has(row.status)) return false;
  if (canDecide) return true;
  if (row.status !== 'requested' || row.requesterId === null || sessionIdentityId === undefined) {
    return false;
  }
  return row.requesterId.toLowerCase() === sessionIdentityId.toLowerCase();
}

/** A szerkesztő-űrlap előtöltése egy igény-sorból (a kategória a típusból, a katalógusból következik). */
export function requestFormFrom(row: AssetRow, lookup: AssetCatalogLookup): AssetRequestFormValues {
  return {
    category: lookup.categoryKeyOfType(row.type) ?? row.category,
    type: row.type,
    name: row.name,
    netPrice: row.netPrice ?? '',
    vatRate: row.vatRate ?? '',
    website: row.website ?? '',
    note: row.requestNote ?? '',
    officeUse: row.officeUse,
    requestedForIdentityId: row.officeUse ? '' : (row.requestedForId ?? ''),
    // személynél a `social` érvénytelen hely (a szerver elutasítaná) → „nincs megadva"
    usageLocation:
      !row.officeUse && row.usageLocation === OFFICE_USAGE ? NONE : (row.usageLocation ?? NONE),
  };
}
